import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Upload, Loader2, X, Check, Image, Trash2, Video, Heart } from 'lucide-react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compressione/taglio video lato client (canvas + MediaRecorder), fallback all'originale
const compressVideo = (file, onProgress, opts = {}) => new Promise((resolve) => {
  try {
    if (typeof MediaRecorder === 'undefined') { resolve(null); return; }
    const startAt = opts.start || 0;
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.playsInline = true;
    video.preload = 'auto';
    const fail = () => { URL.revokeObjectURL(url); resolve(null); };
    const timeout = setTimeout(fail, 90000);
    
    video.onloadedmetadata = () => {
      try {
        const endAt = opts.end && isFinite(opts.end) 
          ? Math.min(opts.end, isFinite(video.duration) ? video.duration : opts.end) 
          : (isFinite(video.duration) ? video.duration : null);
        const maxDim = 1280;
        const scale = Math.min(1, maxDim / Math.max(video.videoWidth, video.videoHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round((video.videoWidth * scale) / 2) * 2;
        canvas.height = Math.round((video.videoHeight * scale) / 2) * 2;
        const ctx = canvas.getContext('2d');
        const canvasStream = canvas.captureStream(30);
        
        // Audio silenzioso tramite AudioContext (non esce dagli altoparlanti)
        let stream = canvasStream;
        let audioCtx = null;
        try {
          audioCtx = new (window.AudioContext || window.webkitAudioContext)();
          const source = audioCtx.createMediaElementSource(video);
          const dest = audioCtx.createMediaStreamDestination();
          source.connect(dest);
          const audioTrack = dest.stream.getAudioTracks()[0];
          if (audioTrack) {
            stream = new MediaStream([...canvasStream.getVideoTracks(), audioTrack]);
          }
          audioCtx.resume?.();
        } catch (e) { /* solo video */ }
        
        const mime = MediaRecorder.isTypeSupported('video/mp4') 
          ? 'video/mp4' 
          : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') 
            ? 'video/webm;codecs=vp8,opus' 
            : 'video/webm';
        
        const recorder = new MediaRecorder(stream, { 
          mimeType: mime, 
          videoBitsPerSecond: 2500000,
          audioBitsPerSecond: 128000
        });
        const chunks = [];
        recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
        recorder.onstop = () => {
          clearTimeout(timeout);
          URL.revokeObjectURL(url);
          audioCtx?.close?.();
          const blob = new Blob(chunks, { type: mime.split(';')[0] });
          const acceptable = opts.force ? blob.size > 0 : (blob.size > 0 && blob.size < file.size * 0.9);
          if (acceptable) {
            const ext = mime.includes('mp4') ? 'mp4' : 'webm';
            resolve(new File([blob], `video_ospite.${ext}`, { type: mime.split(';')[0] }));
          } else {
            resolve(null);
          }
        };
        recorder.onerror = fail;
        
        let rafId;
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          cancelAnimationFrame(rafId);
          video.pause();
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          setTimeout(() => recorder.state !== 'inactive' && recorder.stop(), 300);
        };
        const total = endAt !== null ? (endAt - startAt) : (isFinite(video.duration) ? video.duration : 0);
        const draw = () => {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          if (onProgress && total > 0) {
            onProgress(Math.min(99, Math.round(((video.currentTime - startAt) / total) * 100)));
          }
          if (endAt !== null && video.currentTime >= endAt) { finish(); return; }
          if (video.ended) { finish(); return; }
          rafId = requestAnimationFrame(draw);
        };
        video.onended = finish;
        
        const begin = () => {
          video.play().then(() => {
            recorder.start(500);
            draw();
          }).catch(fail);
        };
        
        if (startAt > 0) {
          video.onseeked = () => { video.onseeked = null; begin(); };
          video.currentTime = startAt;
        } else {
          begin();
        }
      } catch (e) { fail(); }
    };
    video.onerror = fail;
    video.src = url;
  } catch (e) { resolve(null); }
});

// Get or create guest token for this browser
const getGuestToken = () => {
  const STORAGE_KEY = 'instawedding_guest_token';
  let token = localStorage.getItem(STORAGE_KEY);
  if (!token) {
    token = 'guest_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem(STORAGE_KEY, token);
  }
  return token;
};

const GuestGalleryPage = () => {
  const { galleryId } = useParams();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [deleting, setDeleting] = useState(null);
  
  // Get guest token for this browser
  const guestToken = getGuestToken();
  
  useEffect(() => {
    fetchEvent();
  }, [galleryId]);

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API}/public/event/${galleryId}?guest_token=${guestToken}`);
      setEvent(res.data);
      setPhotos(res.data.photos || []);
    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Evento non trovato');
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async (photo) => {
    // Optimistic update
    setPhotos(prev => prev.map(p => p.id === photo.id 
      ? { ...p, liked_by_me: !p.liked_by_me, likes_count: p.likes_count + (p.liked_by_me ? -1 : 1) }
      : p
    ));
    try {
      const formData = new FormData();
      formData.append('guest_token', guestToken);
      const res = await axios.post(`${API}/public/event/${galleryId}/photo/${photo.id}/like`, formData);
      setPhotos(prev => prev.map(p => p.id === photo.id 
        ? { ...p, liked_by_me: res.data.liked, likes_count: res.data.likes_count }
        : p
      ));
    } catch (err) {
      console.error('Error toggling like:', err);
      // Revert
      setPhotos(prev => prev.map(p => p.id === photo.id 
        ? { ...p, liked_by_me: photo.liked_by_me, likes_count: photo.likes_count }
        : p
      ));
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa foto?')) return;
    
    setDeleting(photoId);
    try {
      await axios.delete(`${API}/public/event/${galleryId}/photo/${photoId}?guest_token=${guestToken}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setSelectedPhoto(null);
    } catch (err) {
      console.error('Error deleting photo:', err);
      if (err.response?.status === 403) {
        alert('Non sei autorizzato a eliminare questa foto');
      } else {
        alert('Errore durante l\'eliminazione');
      }
    } finally {
      setDeleting(null);
    }
  };

  // Check if current user owns a photo
  const isOwnPhoto = (photo) => photo.guest_token === guestToken;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-20">
      {/* Header - Instagram style */}
      <header className="sticky top-0 z-30 bg-black border-b border-gray-800">
        <div className="px-4 py-4">
          <div className="text-center">
            <h1 className="font-wedding text-3xl text-white">{event?.title}</h1>
            {event?.event_date && (
              <p className="text-sm text-gray-400 mt-1">
                {new Date(event.event_date).toLocaleDateString('it-IT', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            )}
          </div>
        </div>
        
        {/* Stats bar */}
        <div className="px-4 py-2 border-t border-gray-800">
          <p className="text-center text-sm text-gray-400">
            {photos.length} {photos.length === 1 ? 'foto' : 'foto'} condivise
          </p>
        </div>
      </header>

      {/* Photo Feed - Instagram home style (vertical scroll) */}
      <div className="pb-24">
        {photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <Camera className="w-16 h-16 text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg mb-2">Nessuna foto ancora</p>
            <p className="text-gray-500 text-sm">Sii il primo a condividere un momento!</p>
          </div>
        ) : (
          <div className="max-w-md mx-auto px-8 space-y-5 pt-4">
            {photos.map((photo) => (
              <div 
                key={photo.id}
                className="bg-gray-900 rounded-2xl overflow-hidden"
              >
                {/* Photo Header with guest name and delete button */}
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {photo.guest_name ? (
                      <>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">
                            {photo.guest_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-white font-medium text-sm">
                          {photo.guest_name}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                          <Camera className="w-4 h-4 text-gray-400" />
                        </div>
                        <span className="text-gray-400 text-sm">Ospite</span>
                      </>
                    )}
                  </div>
                  
                  {/* Delete button - only for own photos */}
                  {isOwnPhoto(photo) && (
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={deleting === photo.id}
                      className="p-2 text-gray-500 hover:text-red-500 transition-colors disabled:opacity-50"
                    >
                      {deleting === photo.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </div>
                
                {/* Media - tap photo to view full screen */}
                <div 
                  className={photo.media_type === 'video' ? 'w-full' : 'w-full cursor-pointer'}
                  onClick={() => photo.media_type !== 'video' && setSelectedPhoto(photo)}
                >
                  {photo.media_type === 'video' ? (
                    <video 
                      src={photo.url}
                      className="w-full h-auto"
                      controls
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img 
                      src={photo.url}
                      alt=""
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  )}
                </div>
                
                {/* Photo Footer - likes and timestamp */}
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={() => toggleLike(photo)}
                      data-testid={`like-btn-${photo.id}`}
                      className="transition-transform active:scale-125"
                    >
                      <Heart className={`w-6 h-6 transition-colors ${
                        photo.liked_by_me ? 'fill-red-500 text-red-500' : 'text-white'
                      }`} />
                    </button>
                    {photo.likes_count > 0 && (
                      <span className="text-white text-sm font-medium" data-testid={`likes-count-${photo.id}`}>
                        {photo.likes_count} {photo.likes_count === 1 ? 'mi piace' : 'mi piace'}
                      </span>
                    )}
                  </div>
                  {photo.uploaded_at && (
                    <p className="text-gray-500 text-xs">
                      {new Date(photo.uploaded_at).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'long',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fixed Bottom Bar - Instagram style */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800 px-4 py-3 z-40">
        <button
          onClick={() => setShowUploadModal(true)}
          className="w-full flex items-center justify-center gap-3 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium text-lg"
        >
          <Camera className="w-6 h-6" />
          Condividi foto o video
        </button>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal 
          galleryId={galleryId}
          guestToken={guestToken}
          onClose={() => setShowUploadModal(false)}
          onUploaded={() => {
            fetchEvent();
            setShowUploadModal(false);
          }}
        />
      )}

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white z-10"
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={selectedPhoto.url}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {selectedPhoto.guest_name && (
            <div className="absolute bottom-4 left-4 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
              📷 {selectedPhoto.guest_name}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ galleryId, guestToken, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [guestName, setGuestName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);
  const [compressLabel, setCompressLabel] = useState('Ottimizzazione video...');
  const [sizeInfo, setSizeInfo] = useState(null);
  const [trimData, setTrimData] = useState(null);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(30);
  const trimVideoRef = useRef(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setIsVideo(false);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleVideoChange = (e) => {
    const selectedFile = e.target.files[0];
    e.target.value = '';
    if (!selectedFile) return;
    
    const url = URL.createObjectURL(selectedFile);
    const vid = document.createElement('video');
    vid.preload = 'metadata';
    
    const processVideo = async (duration) => {
      if (duration > 30.5) {
        // Video troppo lungo: apri l'editor di taglio
        setTrimStart(0);
        setTrimEnd(Math.min(30, duration));
        setTrimData({ file: selectedFile, url, duration });
        return;
      }
      
      // Comprimi solo video sopra i 6MB
      let finalFile = selectedFile;
      if (selectedFile.size > 6 * 1024 * 1024) {
        setCompressLabel('Ottimizzazione video...');
        setCompressing(true);
        setCompressProgress(0);
        const compressed = await compressVideo(selectedFile, setCompressProgress);
        setCompressing(false);
        if (compressed) {
          finalFile = compressed;
          setSizeInfo({
            from: (selectedFile.size / (1024 * 1024)).toFixed(1),
            to: (compressed.size / (1024 * 1024)).toFixed(1)
          });
          URL.revokeObjectURL(url);
          setPreview(URL.createObjectURL(compressed));
        } else {
          setSizeInfo(null);
          setPreview(url);
        }
      } else {
        setSizeInfo(null);
        setPreview(url);
      }
      
      setFile(finalFile);
      setIsVideo(true);
      setVideoDuration(duration);
    };
    
    vid.onloadedmetadata = () => {
      if (vid.duration === Infinity || isNaN(vid.duration)) {
        // Workaround per video senza durata nei metadata (es. registrati via MediaRecorder)
        vid.currentTime = 1e101;
        vid.ontimeupdate = () => {
          vid.ontimeupdate = null;
          const d = vid.duration;
          vid.currentTime = 0;
          processVideo(d);
        };
      } else {
        processVideo(vid.duration);
      }
    };
    vid.onerror = () => {
      URL.revokeObjectURL(url);
      alert('Video non valido o formato non supportato');
    };
    vid.src = url;
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const handleTrimChange = ([s, e]) => {
    let start = s;
    let end = e;
    const startMoved = Math.abs(s - trimStart) > Math.abs(e - trimEnd);
    
    // Vincolo: massimo 30 secondi
    if (end - start > 30) {
      if (startMoved) end = start + 30;
      else start = end - 30;
    }
    // Vincolo: minimo 1 secondo
    if (end - start < 1) {
      if (startMoved) start = end - 1;
      else end = start + 1;
    }
    
    setTrimStart(start);
    setTrimEnd(end);
    if (trimVideoRef.current) {
      trimVideoRef.current.currentTime = startMoved ? start : end;
    }
  };

  const cancelTrim = () => {
    URL.revokeObjectURL(trimData.url);
    setTrimData(null);
    setTrimStart(0);
  };

  const confirmTrim = async () => {
    const { file: originalFile, url, duration } = trimData;
    const end = Math.min(trimEnd, duration);
    const start = trimStart;
    setTrimData(null);
    setCompressLabel('Taglio del video...');
    setCompressing(true);
    setCompressProgress(0);
    
    const trimmed = await compressVideo(originalFile, setCompressProgress, {
      start,
      end,
      force: true
    });
    setCompressing(false);
    URL.revokeObjectURL(url);
    
    if (!trimmed) {
      alert('Impossibile tagliare il video su questo dispositivo. Prova con un video più corto.');
      setTrimStart(0);
      return;
    }
    
    if (trimmed.size < originalFile.size) {
      setSizeInfo({
        from: (originalFile.size / (1024 * 1024)).toFixed(1),
        to: (trimmed.size / (1024 * 1024)).toFixed(1)
      });
    } else {
      setSizeInfo(null);
    }
    setFile(trimmed);
    setPreview(URL.createObjectURL(trimmed));
    setIsVideo(true);
    setVideoDuration(end - start);
    setTrimStart(0);
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('guest_name', guestName);
      formData.append('guest_token', guestToken);  // Include guest token for ownership
      if (isVideo) formData.append('duration', String(videoDuration));
      
      await axios.post(`${API}/public/event/${galleryId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        }
      });
      
      setSuccess(true);
      setTimeout(() => {
        onUploaded();
      }, 1500);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Errore durante il caricamento');
      setUploading(false);
    }
  };

  const resetSelection = () => {
    if (isVideo && preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setIsVideo(false);
    setVideoDuration(0);
    setSizeInfo(null);
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <button onClick={onClose} className="text-white">
          <X className="w-6 h-6" />
        </button>
        <span className="text-white font-medium">{file ? (isVideo ? 'Nuovo video' : 'Nuova foto') : 'Condividi'}</span>
        <div className="w-6" />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {success ? (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-white" />
            </div>
            <p className="text-white text-xl font-medium">{isVideo ? 'Video caricato!' : 'Foto caricata!'}</p>
            <p className="text-gray-400 mt-2">Grazie per aver condiviso questo momento</p>
          </div>
        ) : compressing ? (
          <div className="text-center w-full max-w-xs" data-testid="video-compressing">
            <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
            <p className="text-white text-lg font-medium mb-2">{compressLabel}</p>
            <p className="text-gray-400 text-sm mb-4">{compressProgress}%</p>
            <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-200"
                style={{ width: `${compressProgress}%` }}
              />
            </div>
          </div>
        ) : trimData ? (
          <div className="w-full max-w-md" data-testid="video-trimmer">
            <p className="text-white text-lg font-medium text-center mb-1">Video troppo lungo</p>
            <p className="text-gray-400 text-sm text-center mb-4">
              Dura {formatTime(trimData.duration)} — scegli un segmento di massimo 30 secondi
            </p>
            
            <div className="rounded-xl overflow-hidden bg-gray-900 mb-4">
              <video 
                ref={trimVideoRef}
                src={trimData.url}
                className="w-full max-h-64 object-contain"
                controls
                playsInline
                preload="auto"
              />
            </div>
            
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-purple-400 font-medium">
                Da {formatTime(trimStart)}
              </span>
              <span className="bg-gray-800 text-white text-xs px-2 py-1 rounded-full" data-testid="trim-duration">
                {Math.round(trimEnd - trimStart)}s
              </span>
              <span className="text-pink-400 font-medium">
                a {formatTime(trimEnd)}
              </span>
            </div>
            
            <SliderPrimitive.Root
              className="relative flex w-full touch-none select-none items-center h-8 mb-1"
              min={0}
              max={trimData.duration}
              step={0.1}
              value={[trimStart, trimEnd]}
              onValueChange={handleTrimChange}
              data-testid="trim-slider"
            >
              <SliderPrimitive.Track className="relative h-2 w-full grow rounded-full bg-gray-700">
                <SliderPrimitive.Range className="absolute h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb 
                className="block h-6 w-6 rounded-full bg-white shadow-lg focus:outline-none" 
                data-testid="trim-thumb-start"
                aria-label="Inizio"
              />
              <SliderPrimitive.Thumb 
                className="block h-6 w-6 rounded-full bg-white shadow-lg focus:outline-none" 
                data-testid="trim-thumb-end"
                aria-label="Fine"
              />
            </SliderPrimitive.Root>
            <p className="text-gray-500 text-xs text-center mb-5">
              Trascina le maniglie per scegliere inizio e fine (max 30 secondi)
            </p>
            
            <button
              onClick={confirmTrim}
              data-testid="confirm-trim-btn"
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white font-medium flex items-center justify-center gap-2 mb-3"
            >
              <Check className="w-5 h-5" />
              Taglia e usa questo segmento
            </button>
            <button
              onClick={cancelTrim}
              className="w-full py-3 border border-gray-700 rounded-xl text-gray-300"
            >
              Annulla
            </button>
          </div>
        ) : preview ? (
          <div className="w-full max-w-md">
            <div className="relative aspect-square rounded-xl overflow-hidden mb-4 bg-gray-900">
              {isVideo ? (
                <video src={preview} className="w-full h-full object-contain" controls playsInline />
              ) : (
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              )}
              <button 
                onClick={resetSelection}
                className="absolute top-2 right-2 p-2 bg-black/50 rounded-full z-10"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              {isVideo && (
                <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full z-10">
                  {Math.round(videoDuration)}s
                </span>
              )}
            </div>
            
            {sizeInfo && (
              <p className="text-green-400 text-xs text-center mb-3" data-testid="compression-info">
                Video ottimizzato: da {sizeInfo.from} MB a {sizeInfo.to} MB
              </p>
            )}
            
            <input
              type="text"
              placeholder="Il tuo nome (opzionale)"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 mb-4"
            />
            
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium text-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Caricamento... {uploadProgress}%
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Condividi
                </>
              )}
            </button>
            
            {uploading && (
              <div className="mt-4" data-testid="guest-upload-progress">
                <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-200"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full max-w-md space-y-4">
            {/* Camera option */}
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="w-full flex items-center gap-4 p-4 bg-gray-900 rounded-xl border border-gray-700"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Scatta una foto</p>
                <p className="text-gray-400 text-sm">Usa la fotocamera</p>
              </div>
            </button>
            
            {/* Gallery option */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center gap-4 p-4 bg-gray-900 rounded-xl border border-gray-700"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center">
                <Image className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Scegli dalla galleria</p>
                <p className="text-gray-400 text-sm">Seleziona una foto esistente</p>
              </div>
            </button>
            
            {/* Video option */}
            <button
              onClick={() => videoInputRef.current?.click()}
              data-testid="guest-add-video-btn"
              className="w-full flex items-center gap-4 p-4 bg-gray-900 rounded-xl border border-gray-700"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <Video className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Aggiungi un video</p>
                <p className="text-gray-400 text-sm">Massimo 30 secondi</p>
              </div>
            </button>
            
            {/* Hidden inputs */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              className="hidden"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              className="hidden"
              data-testid="guest-video-input"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestGalleryPage;
